import React, {useEffect, useRef, useState} from 'react';
import {
  Animated,
  Easing,
  StyleSheet,
  TouchableWithoutFeedback,
} from 'react-native';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import {Theme} from '../../theme/lightTheme';

interface CustomSwitchProps {
  isEnabled: boolean;
  onToggle: (value: boolean) => void;
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
  activeColor?: string;
  inactiveColor?: string;
  thumbColor?: string;
}

const SwitchContainer = styled.View<{
  height: number;
  isEnabled: boolean;
  activeColor?: string;
  inactiveColor?: string;
  disabled?: boolean;
}>`
  width: 45px;
  height: ${({height}) => height}px;
  border-radius: ${({height}) => height}px;
  padding: 0px;
  background-color: ${({
    theme,
    isEnabled,
    activeColor,
    inactiveColor,
    disabled,
  }) =>
    disabled
      ? theme.text.greyLight + '80'
      : isEnabled
        ? activeColor || theme.switch.trackOn
        : inactiveColor || theme.switch.trackOff};
  justify-content: center;
  opacity: ${({disabled}) => (disabled ? 0.7 : 1)};
  overflow: visible;
`;

const CustomSwitch: React.FC<CustomSwitchProps> = ({
  isEnabled,
  onToggle,
  size = 'medium',
  disabled = false,
  activeColor,
  inactiveColor,
  thumbColor,
}) => {
  const theme = useTheme() as Theme;

  // Fixed width with varying height and thumb size
  const FIXED_WIDTH = 45; // Standard width for all sizes

  // Define sizes based on the size prop - ensuring thumb is larger than height
  const dimensions = {
    small: {height: 20, thumbSize: 24},
    medium: {height: 22, thumbSize: 28},
    large: {height: 28, thumbSize: 34},
  };

  const {height, thumbSize} = dimensions[size];

  // Calculate the exact positions for the thumb
  const leftPosition = 2;
  const rightPosition = FIXED_WIDTH - thumbSize - 2 + thumbSize / 2;

  // Animation values
  const translateX = useRef(
    new Animated.Value(isEnabled ? rightPosition : leftPosition),
  ).current;
  const scaleAnim = useRef(new Animated.Value(1)).current;
  const rotateAnim = useRef(new Animated.Value(0)).current;

  // Static styles
  const [currentThumbColor, setCurrentThumbColor] = useState(
    isEnabled ? theme.switch.thumbOn : theme.switch.thumbOff,
  );

  const shadowValues = {
    shadowOpacity: isEnabled ? 0.35 : 0.25,
    elevation: isEnabled ? 6 : 4,
  };

  // Animation configuration
  const rotateInterpolation = rotateAnim.interpolate({
    inputRange: [-1, 0, 1],
    outputRange: ['-30deg', '0deg', '30deg'],
  });

  // Handle animation when enabled state changes
  useEffect(() => {
    // Update color immediately for responsive feel
    setCurrentThumbColor(
      thumbColor || (isEnabled ? theme.switch.thumbOn : theme.switch.thumbOff),
    );

    // Create a smooth, fast animation sequence
    Animated.parallel([
      // Position transition - quick spring for responsiveness
      Animated.spring(translateX, {
        toValue: isEnabled ? rightPosition : leftPosition,
        friction: 9, // Higher friction for smoother motion
        tension: 70, // Higher tension for faster animation
        useNativeDriver: true,
        velocity: 2, // Higher initial velocity for snappier feel
      }),

      // Thumb animations - simplified for performance
      Animated.sequence([
        // Quick initial scale down - faster than before
        Animated.timing(scaleAnim, {
          toValue: 0.9, // Less extreme scale for smoother feel
          duration: 80, // Faster initial animation
          useNativeDriver: true,
          easing: Easing.out(Easing.quad), // Smoother easing function
        }),

        // Subtle rotation and bounce in one parallel animation
        Animated.parallel([
          // Gentle rotation
          Animated.timing(rotateAnim, {
            toValue: isEnabled ? 0.3 : -0.3, // Less extreme rotation
            duration: 120, // Faster rotation
            useNativeDriver: true,
            easing: Easing.inOut(Easing.quad),
          }),

          // Quick bounce effect
          Animated.spring(scaleAnim, {
            toValue: 1.08, // Less extreme bounce for smoother animation
            friction: 7, // Smoother bounce
            tension: 100, // Faster spring
            useNativeDriver: true,
          }),
        ]),

        // Quick settle
        Animated.parallel([
          // Return to normal size
          Animated.spring(scaleAnim, {
            toValue: 1,
            friction: 6,
            tension: 60,
            useNativeDriver: true,
          }),

          // Return rotation to neutral
          Animated.timing(rotateAnim, {
            toValue: 0,
            duration: 120,
            useNativeDriver: true,
            easing: Easing.out(Easing.quad),
          }),
        ]),
      ]),
    ]).start();
  }, [
    isEnabled,
    rightPosition,
    leftPosition,
    thumbColor,
    theme.switch,
    translateX,
    scaleAnim,
    rotateAnim,
  ]);

  // Optimized toggle handler
  const handleToggle = () => {
    if (!disabled) {
      // Quick press feedback
      Animated.timing(scaleAnim, {
        toValue: 0.95,
        duration: 40, // Faster for immediate feedback
        useNativeDriver: true,
        easing: Easing.linear,
      }).start(() => {
        onToggle(!isEnabled);
      });
    }
  };

  // Touch animations - simplified and optimized
  const handlePressIn = () => {
    Animated.spring(scaleAnim, {
      toValue: 0.95,
      friction: 4,
      tension: 120, // Higher tension for faster animation
      useNativeDriver: true,
    }).start();
  };

  const handlePressOut = () => {
    Animated.spring(scaleAnim, {
      toValue: 1,
      friction: 4,
      tension: 80,
      useNativeDriver: true,
    }).start();
  };

  return (
    <TouchableWithoutFeedback
      onPress={handleToggle}
      disabled={disabled}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}>
      <SwitchContainer
        height={height}
        isEnabled={isEnabled}
        activeColor={activeColor}
        inactiveColor={inactiveColor}
        disabled={disabled}>
        <Animated.View
          style={[
            styles.thumb,
            {
              width: thumbSize,
              height: thumbSize,
              borderRadius: thumbSize / 2,
              backgroundColor: currentThumbColor,
              position: 'absolute',
              top: -((thumbSize - height) / 2),
              left: -thumbSize / 4,
              transform: [
                {translateX},
                {scale: scaleAnim},
                {rotate: rotateInterpolation},
              ],
              shadowColor: theme.text.black,
              shadowOffset: {width: 0, height: 2},
              shadowOpacity: shadowValues.shadowOpacity,
              shadowRadius: 4,
              elevation: shadowValues.elevation,
            },
          ]}
        />
      </SwitchContainer>
    </TouchableWithoutFeedback>
  );
};

const styles = StyleSheet.create({
  thumb: {
    position: 'absolute',
  },
});

export default CustomSwitch;
